// One component, two label variants — no container/bubble/icon (docs/06-
// ux-rules.md "SystemNote style", R4 refined; prototype/v69 `.cv-sys`,
// index.html:1502-1510). `reopened` shares the same typographic treatment
// but has no bold label of its own (P4) — just the body + de-emphasised
// time.
//
// `escalation`'s label renders as the RecoveryAI brand wordmark image, not
// plain text (v70 changelog: "the escalation system-message header uses
// the dark-teal RECOVERYAI wordmark"). Reuses the existing
// `assets/images/recovery-ai/img_wordmark.svg` asset (already used by
// `scenes/OrderFeedback/settings/CurrentSetupCard.tsx` and
// `scenes/OrderFeedback/overview/PromoBlock.tsx`) — no new asset added.
// `automated_ack` is a generic feedback-system notice, not RecoveryAI
// branded, so it keeps its plain-text label.
import imgWordmark from 'assets/images/recovery-ai/img_wordmark.svg'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { formatShortDateTime } from 'utilities/dateFormatters'
import {
  SYSTEM_NOTE_WORDMARK_HEIGHT_PX,
  THREAD_NOTE_MAX_WIDTH_PX,
} from '../constants'

const t = buildTranslate('feedback_inbox.thread')

export type SystemNoteVariant =
  | 'automated_ack'
  | 'escalation'
  | 'reopened'
  | 'restricted'

const LABEL_KEY: Partial<Record<SystemNoteVariant, string>> = {
  automated_ack: 'automated_ack_label',
  escalation: 'escalation_label',
  restricted: 'restricted_note.label',
}

// S4 (AI-1403): a status pill beside the label (v83 "Customer [Restricted]").
const PILL_KEY: Partial<Record<SystemNoteVariant, string>> = {
  restricted: 'restricted_note.pill',
}

export interface SystemNoteProps {
  variant: SystemNoteVariant
  body: string
  at: string
}

const SystemNote = ({ variant, body, at }: SystemNoteProps) => {
  const [css] = useStyletron()
  const labelKey = LABEL_KEY[variant]
  const pillKey = PILL_KEY[variant]

  // In the label row (flex) the gap is a margin; trailing the body inline it's
  // a real space instead — a margin isn't a line-break point, so the body's
  // last word would wrap down with the time (AI-1436).
  const renderTimeSpan = (inline: boolean) => (
    <span
      className={css({
        fontSize: '12px',
        fontWeight: 600,
        color: palette.grey50,
        marginLeft: inline ? 0 : '6px',
        whiteSpace: 'nowrap',
      })}
    >
      · {formatShortDateTime(at)}
    </span>
  )

  // Same treatment as the rail's restricted pill (RightRail/CustomerPanel).
  const pill = pillKey ? (
    <span
      className={css({
        marginLeft: '8px',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: 1.4,
        color: palette.cayenne50,
        backgroundColor: palette.cayenne05,
      })}
    >
      {t(pillKey)}
    </span>
  ) : null

  return (
    <div className={css({ maxWidth: `${THREAD_NOTE_MAX_WIDTH_PX}px` })}>
      {labelKey && (
        // `automated_ack`/`escalation`: label + time share one row
        // (VISUAL-SPEC.md §1 `.cv-sys-head`) — `reopened` has no label of
        // its own, so its time stays inline after the body below instead.
        <div
          className={css({
            display: 'flex',
            alignItems: 'baseline',
            fontSize: '13px',
            fontWeight: 700,
            color: palette.grey70,
            marginBottom: '2px',
          })}
        >
          {variant === 'escalation' ? (
            <img
              src={imgWordmark}
              alt={t(labelKey)}
              className={css({
                height: `${SYSTEM_NOTE_WORDMARK_HEIGHT_PX}px`,
                width: 'auto',
              })}
            />
          ) : (
            <span>{t(labelKey)}</span>
          )}
          {pill}
          {renderTimeSpan(false)}
          {/* TODO(FR-57, Robert): enable when Settings › Guest experience
              lands — see docs/06-ux-rules.md "Configure link" (P6 ✅
              2026-09-18). Pre-wired, commented out; link target = that
              route once it exists.
          {variant === 'automated_ack' && (
            <Button kind="minimal" onClick={onConfigureClick}>
              {t('configure_link')}
            </Button>
          )} */}
        </div>
      )}
      <div
        className={css({
          fontSize: '14px',
          // Mock's body color (`var(--grey60)`) has no palette token — grey70
          // used instead (VISUAL-SPEC.md §1 "Body").
          color: palette.grey70,
          lineHeight: 1.55,
          marginTop: '4px',
        })}
      >
        {body}
        {!labelKey && <> {renderTimeSpan(true)}</>}
      </div>
    </div>
  )
}

export default SystemNote
